// src/pages/shop/components/ChatView.js - PROFESSIONAL REDESIGN
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import {
  User, Package, Send, QrCode, Camera, X, ZoomIn, ShoppingCart,
  CreditCard, Check, CheckCircle, Clock, AlertCircle, MapPin,
  ChevronLeft, MoreVertical, Trash2, ChevronDown, Phone, Mail
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import UnifiedCartWindow from '../../../components/UnifiedCartWindow';

import { 
  collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, 
  doc, updateDoc, getDoc
} from 'firebase/firestore';
import { db, auth } from '../../../firebase/config';
import NotificationService from '../../../services/NotificationService';
import { TransactionService } from '../../../services/TransactionService';
import QrScanner from 'qr-scanner';
import { increment } from 'firebase/firestore';

// Import components

// MODERN STYLED COMPONENTS
const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: linear-gradient(180deg, #0A0A0A 0%, #1A1A1A 100%);
  position: relative;
`;

const ChatHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(20px);
  flex-shrink: 0;
  position: relative;
  z-index: 10;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, 
      transparent 0%, 
      rgba(128, 0, 0, 0.5) 50%, 
      transparent 100%
    );
  }
  
  .left-section {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex: 1;
    min-width: 0;
  }
  
  .back-button {
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.7);
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 8px;
    display: none;
    transition: all 0.2s;
    
    &:hover {
      background: rgba(255, 255, 255, 0.05);
      color: white;
    }
    
    @media (max-width: 768px) {
      display: flex;
    }
  }
  
  .avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: linear-gradient(135deg, #800000, #600000);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #FFFFFF;
    flex-shrink: 0;
    overflow: hidden;
    position: relative;
    border: 2px solid rgba(128, 0, 0, 0.3);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
    
    svg {
      position: absolute;
      display: none;
    }
  }
  
  .chat-details {
    flex: 1;
    min-width: 0;
    
    .item-name {
      font-weight: 100;
      margin-bottom: 0.25rem;
      font-size: 1.1rem;
      color: white;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      letter-spacing: -0.02em;
    }
    
    .seller-info {
      font-size: 0.9rem;
      color: rgba(255, 255, 255, 0.6);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      
      .status-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #4CAF50;
        box-shadow: 0 0 8px rgba(76, 175, 80, 0.6);
      }
    }
  }

  .header-actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    
    .avatar {
      width: 40px;
      height: 40px;
    }
    
    .chat-details {
      .item-name {
        font-size: 0.9rem;
      }

      .seller-info {
        font-size: 0.8rem;
      }
    }
  }
`;

const HeaderButton = styled.button`
  background: ${props => props.primary ? 
    (props.redeem ? 'linear-gradient(135deg, #4CAF50, #45a049)' : 
     'linear-gradient(135deg, #9C27B0, #7B1FA2)') : 
    'transparent'};
  color: white;
  border: ${props => props.primary ? 'none' : '1px solid rgba(255, 255, 255, 0.2)'};
  padding: 0.6rem 1rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => props.primary ? '0 4px 12px rgba(0, 0, 0, 0.3)' : 'none'};
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${props => props.primary ? 
      '0 6px 16px rgba(0, 0, 0, 0.4)' : 
      '0 2px 8px rgba(0, 0, 0, 0.2)'};
    background: ${props => props.primary ? 
      (props.redeem ? 'linear-gradient(135deg, #45a049, #388e3c)' : 
       'linear-gradient(135deg, #7B1FA2, #6A1B9A)') : 
      'rgba(255, 255, 255, 0.05)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.85rem;
    
    svg {
      width: 14px;
      height: 14px;
    }

    .button-text {
      display: none;
    }
  }
`;

const PaymentHandlesWrap = styled.div`
  position: relative;
`;

const PaymentHandlesDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 260px;
  max-width: 80vw;
  background: #14141a;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 12px;
  box-shadow: 0 16px 40px rgba(0,0,0,0.5);
  padding: 1rem;
  z-index: 50;

  .title { font-size: 0.8rem; font-weight: 700; color: #fff; margin-bottom: 0.2rem; }
  .hint  { font-size: 0.68rem; color: rgba(255,255,255,0.4); margin-bottom: 0.6rem; }
  label {
    display: block; font-size: 0.66rem; color: rgba(255,255,255,0.4);
    margin: 0.6rem 0 4px; text-transform: uppercase; letter-spacing: 0.05em;
  }
  input {
    width: 100%; box-sizing: border-box; padding: 0.5rem 0.6rem; border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.12); background: rgba(255,255,255,0.04);
    color: #fff; font-size: 0.82rem;
    &:focus { outline: none; border-color: rgba(156,39,176,0.6); }
  }
  .save-btn {
    margin-top: 0.9rem; width: 100%; padding: 0.55rem; border-radius: 8px; border: none;
    background: linear-gradient(135deg, #9C27B0, #7B1FA2); color: #fff; font-weight: 700; font-size: 0.82rem;
    cursor: pointer;
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }
  .saved-msg { margin-top: 0.5rem; font-size: 0.72rem; color: #4ADE80; }
`;

const ChatBody = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
  background: url('data:image/svg+xml,<svg width="100" height="100" xmlns="http://www.w3.org/2000/svg"><defs><pattern id="grid" width="100" height="100" patternUnits="userSpaceOnUse"><path d="M 100 0 L 0 0 0 100" fill="none" stroke="rgba(255,255,255,0.02)" stroke-width="1"/></pattern></defs><rect width="100%" height="100%" fill="url(%23grid)"/></svg>');
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #800000, #600000);
    border-radius: 10px;
    
    &:hover {
      background: linear-gradient(180deg, #900000, #700000);
    }
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const PendingCreationRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  align-self: center;
  padding: 0.6rem 1.1rem;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85rem;

  .spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.15);
    border-radius: 50%;
    border-top-color: #800000;
    animation: pendingSpin 0.8s linear infinite;
  }

  @keyframes pendingSpin {
    to { transform: rotate(360deg); }
  }
`;

// Reserves scrollable room below an empty conversation — a chat with zero
// messages otherwise has nothing to scroll, which reads as broken rather
// than "nothing said yet".
const EmptyChatRoom = styled.div`
  flex-shrink: 0;
  min-height: 55vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: 1.5rem;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.22);

  span {
    font-size: 0.78rem;
    font-weight: 500;
    letter-spacing: 0.02em;
  }
`;

const Message = styled.div`
  max-width: 75%;
  padding: 1rem 1.25rem;
  border-radius: 16px;
  font-size: 0.95rem;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  position: relative;
  animation: messageSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  @keyframes messageSlideIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  align-self: ${props => props.sent ? 'flex-end' : 'flex-start'};
  background: ${props => props.sent ? 
    'linear-gradient(135deg, #800000, #600000)' : 
    'rgba(255, 255, 255, 0.05)'
  };
  color: white;
  box-shadow: ${props => props.sent ?
    '0 4px 12px rgba(128, 0, 0, 0.3)' :
    '0 2px 8px rgba(0, 0, 0, 0.2)'
  };
  border: 1px solid ${props => props.sent ?
    'rgba(128, 0, 0, 0.5)' :
    'rgba(255, 255, 255, 0.08)'
  };
  
  ${props => props.sent ? 
    'border-bottom-right-radius: 4px;' : 
    'border-bottom-left-radius: 4px;'
  }
  
  .message-time {
    margin-top: 0.75rem;
    font-size: 0.75rem;
    opacity: 0.6;
    text-align: right;
    font-weight: 500;
  }

  &.system-message {
    align-self: center;
    background: linear-gradient(135deg, rgba(33, 150, 243, 0.15), rgba(0, 188, 212, 0.15));
    border: 1px solid rgba(33, 150, 243, 0.3);
    color: #64B5F6;
    font-weight: 500;
    max-width: 85%;
    text-align: center;
    
    .message-time {
      text-align: center;
      color: rgba(100, 181, 246, 0.8);
    }
  }

  &.system-action-window {
    align-self: center;
    width: 90%;
    max-width: 90%;
    padding: 0;
    background: transparent;
    box-shadow: none;
    border: none;
  }

  &.qr-message {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(139, 195, 74, 0.1));
  border: 2px solid rgba(76, 175, 80, 0.3);
  text-align: center;
  max-width: 90%;
  padding: 2rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  align-self: center;

  &:hover {
    background: linear-gradient(135deg, rgba(76, 175, 80, 0.15), rgba(139, 195, 74, 0.15));
    transform: scale(1.02);
    box-shadow: 0 8px 24px rgba(76, 175, 80, 0.2);
  }

  .qr-title {
    color: #4CAF50;
    font-weight: 700;
    margin-bottom: 1.5rem;
    font-size: 1.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
  }

  .qr-image {
    background: white;
    padding: 1.5rem;
    border-radius: 16px;
    margin: 1.5rem auto;
    display: inline-block;
    position: relative;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);

    img {
      display: block;
      width: ${props => props.expanded ? '300px' : '200px'};
      height: ${props => props.expanded ? '300px' : '200px'};
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .expand-hint {
      position: absolute;
      bottom: 0.75rem;
      right: 0.75rem;
      background: rgba(0, 0, 0, 0.8);
      padding: 0.35rem 0.75rem;
      border-radius: 8px;
      font-size: 0.75rem;
      display: flex;
      align-items: center;
      gap: 0.35rem;
      color: white;
      font-weight: 600;
    }
  }

  .qr-code-text {
    font-family: 'SF Mono', Monaco, 'Courier New', monospace;
    font-size: 1.4rem;
    font-weight: 700;
    letter-spacing: 3px;
    color: #4CAF50;
    margin: 1.5rem 0;
    padding: 0.75rem 1.5rem;
    background: rgba(76, 175, 80, 0.1);
    border-radius: 12px;
    border: 2px dashed rgba(76, 175, 80, 0.3);
  }

  .qr-instructions {
    font-size: 0.95rem;
    opacity: 0.9;
    color: rgba(255, 255, 255, 0.9);
    margin-top: 1.5rem;
    line-height: 1.6;
    font-weight: 500;
  }
  
  .message-time {
    margin-top: 1rem;
    font-size: 0.75rem;
    opacity: 0.6;
    text-align: center;
    color: rgba(255, 255, 255, 0.7);
  }
}

@media (max-width: 768px) {
  max-width: 85%;
  padding: 0.85rem 1rem;
  font-size: 0.9rem;

  &.qr-message {
    padding: 1.5rem;
    max-width: 95%;
    
    .qr-title {
      font-size: 1rem;
      margin-bottom: 1rem;
    }
    
    .qr-image {
      padding: 1rem;
      margin: 1rem auto;
      
      img {
        width: ${props => props.expanded ? '250px' : '180px'};
        height: ${props => props.expanded ? '250px' : '180px'};
      }
      
      .expand-hint {
        font-size: 0.65rem;
        padding: 0.25rem 0.5rem;
      }
    }

    .qr-code-text {
      font-size: 1.1rem;
      letter-spacing: 2px;
      padding: 0.6rem 1rem;
      margin: 1rem 0;
    }
    
    .qr-instructions {
      font-size: 0.85rem;
      margin-top: 1rem;
    }
  }
}

  @media (max-width: 768px) {
    max-width: 85%;
    padding: 0.85rem 1rem;
    font-size: 0.9rem;

    &.qr-message {
      padding: 1.5rem;
      
      .qr-image img {
        width: ${props => props.expanded ? '250px' : '180px'};
        height: ${props => props.expanded ? '250px' : '180px'};
      }

      .qr-code-text {
        font-size: 1.2rem;
        letter-spacing: 2px;
      }
    }
    
    &.system-action-window {
      width: 95%;
      max-width: 95%;
    }
  }
`;

const CartSummary = styled.div`
  background: linear-gradient(135deg, rgba(0, 188, 212, 0.1), rgba(0, 150, 136, 0.1));
  border: 2px solid rgba(0, 188, 212, 0.3);
  border-radius: 16px;
  padding: 1.5rem;
  margin: 1rem 0;
  align-self: center;
  width: 85%;
  box-shadow: 0 4px 16px rgba(0, 188, 212, 0.2);
  
  .cart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid rgba(0, 188, 212, 0.2);
    
    h4 {
      color: #00BCD4;
      margin: 0;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      font-size: 1.1rem;
      font-weight: 700;
      letter-spacing: -0.02em;
    }
  }
  
  .cart-items {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }
  
  .cart-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    background: rgba(0, 0, 0, 0.3);
    border-radius: 12px;
    border: 1px solid rgba(0, 188, 212, 0.2);
    transition: all 0.2s;
    
    &:hover {
      background: rgba(0, 0, 0, 0.4);
      border-color: rgba(0, 188, 212, 0.4);
    }
    
    .item-info {
      display: flex;
      align-items: center;
      gap: 1rem;
      flex: 1;
      min-width: 0;
      
      img {
        width: 56px;
        height: 56px;
        border-radius: 10px;
        object-fit: cover;
        flex-shrink: 0;
        border: 2px solid rgba(0, 188, 212, 0.3);
      }
      
      .item-details {
        flex: 1;
        min-width: 0;
        
        .item-name {
          font-weight: 600;
          font-size: 0.95rem;
          margin-bottom: 0.35rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          color: white;
        }
        
        .item-quantity {
          font-size: 0.85rem;
          opacity: 0.8;
          color: rgba(255, 255, 255, 0.7);
        }
      }
    }
    
    .item-price {
      font-weight: 700;
      color: #00BCD4;
      font-size: 1rem;
      white-space: nowrap;
      margin-left: 1rem;
    }
  }
  
  .cart-total {
    border-top: 2px solid rgba(0, 188, 212, 0.3);
    padding-top: 1rem;
    display: flex;
    justify-content: space-between;
    font-weight: 700;
    font-size: 1.2rem;
    color: #00BCD4;
  }
  
  @media (max-width: 768px) {
    width: 90%;
    padding: 1.25rem;
    
    .cart-item {
      padding: 0.85rem;
      
      .item-info {
        gap: 0.75rem;
        
        img {
          width: 48px;
          height: 48px;
        }
        
        .item-details {
          .item-name {
            font-size: 0.9rem;
          }
          
          .item-quantity {
            font-size: 0.8rem;
          }
        }
      }
      
      .item-price {
        font-size: 0.95rem;
      }
    }
  }
`;

const SendCartButton = styled.button`
  width: 85%;
  align-self: center;
  padding: 1.25rem;
  background: linear-gradient(135deg, #00BCD4, #0097A7);
  color: white;
  border: none;
  border-radius: 16px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 16px rgba(0, 188, 212, 0.3);
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 188, 212, 0.4);
    background: linear-gradient(135deg, #00ACC1, #00838F);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    width: 90%;
    padding: 1rem;
    font-size: 0.95rem;
  }
`;

const ChatInput = styled.div`
  padding: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(20px);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, 
      transparent 0%, 
      rgba(128, 0, 0, 0.5) 50%, 
      transparent 100%
    );
  }
  
  input {
    flex: 1;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 24px;
    padding: 0.85rem 1.5rem;
    color: #FFFFFF;
    font-size: 0.95rem;
    transition: all 0.3s;
    
    &:focus {
      outline: none;
      border-color: rgba(128, 0, 0, 0.5);
      background: rgba(255, 255, 255, 0.08);
      box-shadow: 0 0 0 3px rgba(128, 0, 0, 0.1);
    }
    
    &::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }
  }
  
  .send-button {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: linear-gradient(135deg, #800000, #600000);
    color: white;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 12px rgba(128, 0, 0, 0.3);
    
    &:hover:not(:disabled) {
      transform: scale(1.1);
      box-shadow: 0 6px 16px rgba(128, 0, 0, 0.4);
      background: linear-gradient(135deg, #900000, #700000);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  }

  @media (max-width: 768px) {
    padding: 1rem;
    
    input {
      padding: 0.75rem 1.25rem;
      font-size: 0.9rem;
    }
    
    .send-button {
      width: 40px;
      height: 40px;
    }
  }
`;

// Camera Modal and Redeem Modal styles remain similar but with updated gradients and shadows
const RedeemModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .modal-content {
    background: linear-gradient(135deg, #1A1A1A, #0A0A0A);
    border: 2px solid rgba(76, 175, 80, 0.3);
    border-radius: 24px;
    padding: 2.5rem;
    max-width: 500px;
    width: 100%;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
    animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    h3 {
      color: #4CAF50;
      margin-bottom: 1.5rem;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      font-size: 1.5rem;
      font-weight: 700;
      letter-spacing: -0.02em;
    }

    .code-input-section {
      margin-bottom: 1.5rem;

      label {
        display: block;
        margin-bottom: 0.75rem;
        color: rgba(255, 255, 255, 0.9);
        font-size: 0.95rem;
        font-weight: 600;
      }

      input {
        width: 100%;
        padding: 1rem;
        background: rgba(255, 255, 255, 0.05);
        border: 2px solid rgba(255, 255, 255, 0.1);
        border-radius: 12px;
        color: white;
        font-family: 'SF Mono', Monaco, monospace;
        font-size: 1.3rem;
        text-align: center;
        letter-spacing: 4px;
        transition: all 0.3s;

        &:focus {
          outline: none;
          border-color: #4CAF50;
          background: rgba(76, 175, 80, 0.05);
          box-shadow: 0 0 0 4px rgba(76, 175, 80, 0.1);
        }
      }
    }

    .error-message {
      color: #F44336;
      background: rgba(244, 67, 54, 0.1);
      border: 1px solid rgba(244, 67, 54, 0.3);
      padding: 1rem;
      border-radius: 12px;
      margin-bottom: 1rem;
      font-weight: 500;
    }

    .success-message {
      color: #4CAF50;
      background: rgba(76, 175, 80, 0.1);
      border: 1px solid rgba(76, 175, 80, 0.3);
      padding: 1rem;
      border-radius: 12px;
      margin-bottom: 1rem;
      font-weight: 600;
    }

    .modal-buttons {
      display: flex;
      gap: 0.75rem;

      button {
        flex: 1;
        padding: 1rem;
        border: none;
        border-radius: 12px;
        font-weight: 700;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

        &.cancel {
          background: transparent;
          border: 2px solid rgba(255, 255, 255, 0.2);
          color: white;
          
          &:hover {
            background: rgba(255, 255, 255, 0.05);
            border-color: rgba(255, 255, 255, 0.3);
          }
        }

        &.scan {
          background: linear-gradient(135deg, #2196F3, #1976D2);
          color: white;
          box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
          
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(33, 150, 243, 0.4);
          }
        }

        &.verify {
          background: linear-gradient(135deg, #4CAF50, #388E3C);
          color: white;
          box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
          
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(76, 175, 80, 0.4);
          }
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }
      }
    }
  }

  @media (max-width: 768px) {
    .modal-content {
      padding: 2rem;
    }
  }
`;

const CameraModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;

  .camera-header {
    color: white;
    text-align: center;
    margin-bottom: 2rem;

    h3 {
      margin-bottom: 0.75rem;
      color: #4CAF50;
      font-size: 1.5rem;
      font-weight: 700;
    }
    
    p {
      opacity: 0.8;
      font-size: 0.95rem;
      margin-bottom: 0.5rem;
    }
  }

  .camera-view {
    position: relative;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);

    video {
      width: 300px;
      height: 300px;
      object-fit: cover;
      background: #000;
      border: 3px solid #4CAF50;
    }

    .scan-overlay {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 200px;
      height: 200px;
      border: 3px solid #4CAF50;
      border-radius: 20px;
      box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.6);
      
      &::before, &::after {
        content: '';
        position: absolute;
        width: 24px;
        height: 24px;
        border: 4px solid #4CAF50;
        border-radius: 4px;
      }
      
      &::before {
        top: -4px;
        left: -4px;
        border-right: transparent;
        border-bottom: transparent;
      }
      
      &::after {
        bottom: -4px;
        right: -4px;
        border-left: transparent;
        border-top: transparent;
      }
    }
  }

  .camera-controls {
    display: flex;
    gap: 1rem;
    margin-top: 2rem;

    button {
      padding: 1rem 2rem;
      border: none;
      border-radius: 12px;
      font-weight: 700;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      background: linear-gradient(135deg, #f44336, #da190b);
      color: white;
      box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3);
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(244, 67, 54, 0.4);
      }
    }
  }

  @media (max-width: 768px) {
    padding: 0.5rem;
    
    .camera-view {
      video {
        width: 250px;
        height: 250px;
      }
      
      .scan-overlay {
        width: 150px;
        height: 150px;
      }
    }
  }
`;



const ChatView = ({ chat, isMobile, onBackToList, activeShopId, activeShopName }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Once the unified order window has appeared for this chat, keep it mounted —
  // otherwise transient listener updates (chat doc vs transaction doc arriving
  // at slightly different times) can flip the render gate false→true→false,
  // unmounting/remounting the whole window and forcing every image to reload
  // (visible as flicker). Only resets when the user switches to a different chat.
  const [hasOrderWindow, setHasOrderWindow] = useState(false);
  // When the chat ID changes, initialise the window based on whether the
  // incoming chat already carries order data. Blindly resetting to false
  // caused a one-frame flash where the window disappeared (showing the chat
  // backdrop) whenever the optimistic temp-ID resolved to the real chat ID.
  useEffect(() => {
    const hasOrderData = !!(chat?.pendingPurchase || chat?.cartItems?.length > 0);
    setHasOrderWindow(hasOrderData);
  }, [chat?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const shouldShow = !!(chat?.pendingPurchase || chat?.cartItems?.length > 0
      || transactionDetails?.cartItems?.length > 0
      || transactionDetails?.isClaim || transactionDetails?.isService);
    if (shouldShow) setHasOrderWindow(true);
  }, [chat?.id, chat?.pendingPurchase, chat?.cartItems, transactionDetails?.cartItems, transactionDetails?.isClaim, transactionDetails?.isService]);
  
  // Action window states
  const [showSellerPrompt, setShowSellerPrompt] = useState(true);
  const [maxAvailableQuantity, setMaxAvailableQuantity] = useState(0);
  const [showPaymentPrompt, setShowPaymentPrompt] = useState(false);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  
  // Seller's pickup payment handles (Zelle/Venmo/other) — shown on the buyer's payment options
  const [showPaymentHandles, setShowPaymentHandles] = useState(false);
  const [paymentHandlesForm, setPaymentHandlesForm] = useState({ zelleId: '', venmoId: '', other: '' });
  const [savingHandles, setSavingHandles] = useState(false);
  const [handlesSaved, setHandlesSaved] = useState(false);

  useEffect(() => {
    if (!showPaymentHandles || !chat?.sellerId) return;
    setHandlesSaved(false);
    getDoc(doc(db, 'shops', chat.sellerId)).then(snap => {
      if (snap.exists()) {
        const h = snap.data().paymentHandles || {};
        setPaymentHandlesForm({ zelleId: h.zelleId || '', venmoId: h.venmoId || '', other: h.other || '' });
      }
    }).catch(() => {});
  }, [showPaymentHandles, chat?.sellerId]);

  const handleSavePaymentHandles = async () => {
    if (!chat?.sellerId) return;
    setSavingHandles(true);
    try {
      await TransactionService.updatePaymentHandles(chat.sellerId, paymentHandlesForm);
      setHandlesSaved(true);
    } catch (e) {
      alert('Could not save payment info: ' + (e.message || 'Unknown error'));
    } finally {
      setSavingHandles(false);
    }
  };

  // Redeem state
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [redeemCode, setRedeemCode] = useState('KODE-');
  const [redeemError, setRedeemError] = useState(null);
  const [redeemSuccess, setRedeemSuccess] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const currentUserRef = useRef(null); 

  // QR expansion
  const [expandedQR, setExpandedQR] = useState(false);
  const [otherPartyProfile, setOtherPartyProfile] = useState(null); // ✅ NEW

  const messagesEndRef = useRef(null);
  const videoRef = useRef(null);
  const qrScannerRef = useRef(null);
  const chatBodyRef = useRef(null);
  const chatInputRef = useRef(null);
  const isScrolledUpRef = useRef(false);
  const [sendingCart, setSendingCart] = useState(false);
  const [cartSent, setCartSent] = useState(false); 
  
  // Add these refs to prevent re-renders
  const chatIdRef = useRef(null);
  const unsubscribeRefs = useRef([]);

  const shouldShowContent = (contentType, chat, currentUser) => {
  if (!currentUser || !chat) return false;
  
  // ✅ FORCE GUESTS TO ONLY SEE BUYER CONTENT
  if (currentUser.type === 'guest') {
    const buyerOnlyContent = [
      'payment_window',
      'withdraw_button',
      'cart_summary',
      'send_cart_button',
      'chat_input'
    ];
    
    if (!buyerOnlyContent.includes(contentType)) {
      return false;
    }
  }
  
  // ✅ USE ONLY currentUser.role - SINGLE SOURCE OF TRUTH
  const userRole = currentUser.role;
  const chatStatus = chat.status || chat.transactionStatus || transactionDetails?.status;
  
  switch (contentType) {
    case 'cart_summary':
      return userRole === 'buyer' && 
             chatStatus === 'pending_send' &&
             !cartSent;
    
    case 'send_cart_button':
      return userRole === 'buyer' && 
             chatStatus === 'pending_send' &&
             !cartSent;
    
    case 'seller_response':
      return userRole === 'seller' && 
             (chatStatus === 'pending_seller_acceptance' ||
              transactionDetails?.status === 'pending_seller_acceptance');
    
    case 'payment_window':
      return userRole === 'buyer' && 
             (chatStatus === 'seller_accepted' ||
              transactionDetails?.status === 'seller_accepted');
    
    case 'location_window':
      return userRole === 'seller' && 
             (chatStatus === 'paid' ||
              transactionDetails?.status === 'paid');
    
    case 'withdraw_button':
      return userRole === 'buyer' && 
             (chatStatus === 'paid' ||
              transactionDetails?.status === 'paid');
    
    case 'redeem_button':
      return userRole === 'seller' && 
             (chatStatus === 'paid' ||
              transactionDetails?.status === 'paid');
    
    case 'chat_input':
      const hideInputStatuses = ['completed', 'seller_rejected', 'withdrawn'];
      return !hideInputStatuses.includes(chatStatus);
    
    default:
      return true;
  }
};
  
  const getCurrentUserInfo = useCallback(() => {
  const user = auth.currentUser;

  if (!user) return null;

  // ✅ SIMPLIFIED: Works for both authenticated and anonymous
  const userId = user.uid;  // Firebase UID (always present)
  const isGuest = user.isAnonymous;  // ✅ Simple check

  // When acting as a shared shop seller, use the shop name as display name
  // Only treat as acting-as-shop when the user is genuinely the seller side;
  // if they are the actual buyer of this chat, always use their own identity.
  const isActingAsShop = activeShopId &&
    activeShopId !== userId &&
    chat?.sellerId === activeShopId &&
    chat?.buyerId !== userId;
  const userName = isActingAsShop
    ? (activeShopName || user.displayName || user.email || 'Shop')
    : (user.displayName || user.email || 'Guest');

  console.log('👤 User info:', {
    uid: userId,
    name: userName,
    isAnonymous: user.isAnonymous,
    email: user.email,
    actingAsShop: isActingAsShop
  });

  // Get role from chat
  let userRole = null;

  // Shared user acting as the shop seller
  if (isActingAsShop) {
    userRole = 'seller';
  } else if (chat?.roles && chat.roles[userId]) {
    userRole = chat.roles[userId];
  } else if (chat?.buyerId === userId) {
    userRole = 'buyer';
  } else if (chat?.sellerId === userId) {
    userRole = 'seller';
  }

  // The effective "self" ID for unread marking:
  // when acting as shop, use the shop's seller ID so unread count is cleared correctly
  const effectiveId = isActingAsShop ? activeShopId : userId;

  return {
    id: effectiveId,
    uid: userId,          // the real user UID (for audit trail)
    senderUid: userId,    // always the real UID when writing messages
    name: userName,
    role: userRole,
    type: isGuest ? 'guest' : 'authenticated',
    isGuest: isGuest,
  };
}, [chat?.id, chat?.buyerId, chat?.sellerId, chat?.roles]);
  
  // ✅ FIX: Use useMemo with explicit dependencies
  const currentUser = useMemo(() => {
    return getCurrentUserInfo();
  }, [chat?.id, chat?.sellerId, chat?.buyerId, chat?.isGuestOrder, activeShopId, activeShopName]);

  useEffect(() => {
    if (chat?.isCart && chat?.status !== 'pending_send') {
      setCartSent(true); // Cart already sent if status changed
    }
  }, [chat?.isCart, chat?.status]);
  
  // Add effect to listen for chat updates
  useEffect(() => {
  // Real doc doesn't exist yet — skip until goToRealChat swaps in the real id
  if (!chat?.id || !currentUser || chat.__pendingCreation) return;

  // Listen for chat status changes
  const chatRef = doc(db, 'chats', chat.id);
  
  const unsubscribe = onSnapshot(chatRef, (docSnap) => {
    if (docSnap.exists()) {
      const chatData = docSnap.data();
      
      console.log('📊 Chat status update:', {
        status: chatData.status,
        isCart: chatData.isCart,
        wasCartSent: cartSent
      });
      
      // ✅ If status changed from 'pending_send', hide cart immediately
      if (chatData.status !== 'pending_send') {
        setCartSent(true);
      }
      
      // ✅ Update chat object to trigger re-render
      if (chat) {
        Object.assign(chat, chatData);
      }
    }
  });
  
  return () => unsubscribe();
}, [chat?.id, currentUser]);

  // ✅ Log only when currentUser actually changes
  useEffect(() => {
    if (currentUser) {
      console.log('👤 Current user updated:', {
        chatId: chat?.id,
        userType: currentUser.type,
        userId: currentUser.id,
        role: currentUser.role
      });
    }
  }, [currentUser?.id, currentUser?.type, chat?.id]);

  const isGuestUser = chat?.isGuestOrder && chat?.buyerId?.startsWith('guest_');

  // ✅ FIX: Update messages listener with better cleanup
  useEffect(() => {
  // Real doc doesn't exist yet — skip until goToRealChat swaps in the real id
  if (!chat?.id || !currentUser || chat.__pendingCreation) return;

  console.log('🔄 Setting up messages listener for:', currentUser.id);
  
  const messagesRef = collection(db, 'chats', chat.id, 'messages');
  const q = query(messagesRef, orderBy('timestamp', 'asc'));
  
  const unsubscribe = onSnapshot(q, 
    (snapshot) => {
      const messageData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp
      }));
      
      setMessages(messageData);
      
      // Mark as read
      if (chat.unreadCount > 0) {
        updateDoc(doc(db, 'chats', chat.id), {
          [`unreadCount.${currentUser.id}`]: 0
        }).catch(err => console.error('Error marking as read:', err));
      }
    },
    (error) => {
      console.error('❌ Messages listener error:', error);
    }
  );
  
  return () => unsubscribe();
}, [chat?.id, currentUser?.id]);

useEffect(() => {
  const fetchOtherPartyProfile = async () => {
    if (!chat) return;
    
    try {
      const otherPartyId = chat.role === 'buyer' ? chat.sellerId : chat.buyerId;
      
      // Skip if guest order
      if (chat.isGuestOrder && chat.role === 'seller') {
        setOtherPartyProfile(null);
        return;
      }
      
      const shopRef = doc(db, 'shops', otherPartyId);
      const shopSnap = await getDoc(shopRef);

      if (shopSnap.exists() && shopSnap.data().profile) {
        setOtherPartyProfile(shopSnap.data().profile);
        return;
      }

      // Fallback: other party has no shop pic (e.g. a buyer with no shop) — use their personal photo
      const userSnap = await getDoc(doc(db, 'users', otherPartyId));
      if (userSnap.exists()) {
        const userData = userSnap.data();
        setOtherPartyProfile(userData.photoURL || userData.profilePic || userData.profile || null);
      } else {
        setOtherPartyProfile(null);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };
  
  fetchOtherPartyProfile();
}, [chat]);

// 5. ADD helper function to calculate item count (add before renderMessage)
const getItemCount = useCallback((chat) => {
  if (chat.cartItems && chat.cartItems.length > 0) {
    return chat.cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
  }
  
  if (chat.pendingPurchase) {
    const qty = chat.pendingPurchase.approvedQuantity || 
                chat.pendingPurchase.requestedQuantity || 
                chat.pendingPurchase.quantity || 1;
    return qty;
  }
  
  if (transactionDetails) {
    const qty = transactionDetails.approvedQuantity || 
                transactionDetails.requestedQuantity || 
                transactionDetails.quantity || 1;
    return qty;
  }
  
  return 1;
}, [chat, transactionDetails]);

// ✅ NEW: Real-time chat status listener for guests (using Firestore directly)
useEffect(() => {
  // ✅ Skip Firestore listener for guests (they use polling)
  // Also skip while the chat doc is still an optimistic placeholder — the real doc
  // lives at a different id, and duplicating the listener at 1258-1289 that swaps
  // targets rapidly during the optimistic → real transition can trip a Firestore
  // SDK internal assertion (`INTERNAL ASSERTION FAILED: Unexpected state`).
  if (currentUser?.type === 'guest' || !chat?.id || chat.__pendingCreation) return;

  console.log('👤 Setting up real-time chat listener for authenticated user:', chat.id);
  
  // Use Firestore listener for chat document (authenticated users only)
  const chatRef = doc(db, 'chats', chat.id);
  
  const unsubscribe = onSnapshot(
    chatRef,
    (docSnap) => {
      if (docSnap.exists()) {
        const chatData = docSnap.data();
        
        console.log('📊 Chat update detected:', {
          status: chatData.status,
          transactionStatus: chatData.transactionStatus,
          lastMessage: chatData.lastMessage
        });
        
        // Update local chat object when status changes
        const currentStatus = chat.status || chat.transactionStatus;
        const newStatus = chatData.status || chatData.transactionStatus;
        
        if (currentStatus !== newStatus) {
          console.log('🔄 Status changed:', currentStatus, '→', newStatus);
          
          // Update chat object
          if (chat && typeof chat === 'object') {
            Object.assign(chat, {
              status: chatData.status,
              transactionStatus: chatData.transactionStatus,
              lastMessage: chatData.lastMessage,
              lastMessageTime: chatData.lastMessageTime,
              pendingPurchase: chatData.pendingPurchase
            });
          }
        }
      }
    },
    (error) => {
      console.error('❌ Chat listener error:', error);
      // Don't show error to user
    }
  );
  
  return () => {
    console.log('🔌 Cleaning up chat listener');
    unsubscribe();
  };
}, [chat?.id, currentUser?.type]);


// ✅ NEW: Separate function to fetch messages (called on-demand)
const fetchGuestMessages = useCallback(async () => {
  if (!chat?.id || currentUser?.type !== 'guest') return;
  
  try {
    const guestSession = localStorage.getItem('guest_session');
    if (!guestSession) return;
    
    const guestInfo = JSON.parse(guestSession);
    
    const response = await fetch('https://www.kalkode.com/api/guest-operations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        operation: 'get_messages',
        chatId: chat.id,
        guestId: guestInfo.guestId
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Failed to fetch messages:', errorText);
      return;
    }
    
    const data = await response.json();
    
    if (data.messages && Array.isArray(data.messages)) {
      const messagesWithDates = data.messages.map(msg => ({
        ...msg,
        timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date()
      }));
      
      setMessages(messagesWithDates);
      console.log(`✅ Fetched ${messagesWithDates.length} messages`);
    }
  } catch (error) {
    console.error('❌ Error fetching guest messages:', error);
  }
}, [chat?.id, currentUser?.type]);

// ✅ NEW: Initial message fetch on mount
useEffect(() => {
  if (currentUser?.type === 'guest' && chat?.id) {
    console.log('📥 Initial message fetch for guest');
    fetchGuestMessages();
  }
}, [chat?.id, currentUser?.type, fetchGuestMessages]);


useEffect(() => {
  if (messages.length > 0 && currentUser) {
    console.log('🔍 Message visibility check:', {
      totalMessages: messages.length,
      currentUserId: currentUser.id,
      currentUserRole: currentUser.role,
      visibleMessages: messages.filter(msg => {
        if (!msg.visibleTo || msg.visibleTo.length === 0) return true;
        return msg.visibleTo.includes(currentUser.id);
      }).length,
      qrMessages: messages.filter(msg => msg.type === 'payment_qr_code').length,
      visibleQR: messages.filter(msg => 
        msg.type === 'payment_qr_code' && 
        (!msg.visibleTo || msg.visibleTo.includes(currentUser.id))
      ).length
    });
  }
}, [messages, currentUser]);

  useEffect(() => {
  if (isGuestUser) {
    const guestSession = localStorage.getItem('guest_session');
    console.log('🔍 Guest session check:', {
      hasSession: !!guestSession,
      session: guestSession ? JSON.parse(guestSession) : null,
      chatBuyerId: chat?.buyerId,
      isGuestUser
    });
  }
}, [chat, isGuestUser]);


// ✅ ADD NEW useEffect for guest transaction polling
useEffect(() => {
    // Real doc doesn't exist yet — skip until goToRealChat swaps in the real id
    if (currentUser?.type === 'authenticated' && chat?.transactionId && !chat.__pendingCreation) {
      const listenerKey = `transaction_${chat.transactionId}`;
      
      if (chatIdRef.current === listenerKey) {
        console.log('⏭️ Already listening to transaction:', chat.transactionId);
        return;
      }
      
      console.log('🔄 Setting up transaction listener:', chat.transactionId);
      
      // Clean up old transaction listener
      if (unsubscribeRefs.current.length > 0) {
        unsubscribeRefs.current.forEach(unsub => unsub());
        unsubscribeRefs.current = [];
      }
      
      chatIdRef.current = listenerKey;
      
      const unsubscribe = onSnapshot(
        doc(db, 'transactions', chat.transactionId),
        (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            
            setTransactionDetails(prev => {
              if (JSON.stringify(prev) === JSON.stringify(data)) {
                return prev;
              }
              
              console.log('🔔 Transaction update:', data.status);
              
              if (data.status === 'seller_accepted' && chat.isBuyer) {
                setShowPaymentPrompt(true);
              }
              
              if (data.status === 'paid') {
                setShowPaymentPrompt(false);
              }
              
              return data;
            });
          }
        }
      );
      
      unsubscribeRefs.current.push(unsubscribe);
      
      return () => {
        console.log('🔌 Cleaning up transaction listener');
        unsubscribeRefs.current.forEach(unsub => unsub());
        unsubscribeRefs.current = [];
        chatIdRef.current = null;
      };
    }
    
    return () => {};
  }, [chat?.transactionId, chat?.isBuyer, currentUser?.type]);

  useEffect(() => {
    return () => {
      if (unsubscribeRefs.current.length > 0) {
        console.log('🧹 Component unmounting - cleaning up all listeners');
        unsubscribeRefs.current.forEach(unsubscribe => unsubscribe());
        unsubscribeRefs.current = [];
      }
      chatIdRef.current = null;
      currentUserRef.current = null;
    };
  }, []);
  
  // Optimize checkPaymentStatus function
  const checkPaymentStatus = useCallback(async () => {
    if (!chat?.transactionId || chat.__pendingCreation) return;
    
    try {
      const transactionDoc = await getDoc(doc(db, 'transactions', chat.transactionId));
      if (transactionDoc.exists()) {
        const data = transactionDoc.data();
        const isService = data.isService || false;
        
        console.log('🔍 Current transaction status:', {
          status: data.status,
          isService,
          type: isService ? 'service' : 'item'
        });
        
        if (data.status === 'paid' && showPaymentPrompt) {
          setShowPaymentPrompt(false);
          
          if (data.transactionCode && !messages.some(m => m.type === 'payment_qr_code')) {
            await addQRCodeToChat(data.transactionCode, data.qrCodeUrl);
          }
        }
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  }, [chat?.transactionId, messages, showPaymentPrompt]);
  
  // Check payment status with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      checkPaymentStatus();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [chat?.transactionId, messages]);
  
  const handleSendCart = async () => {
  try {
    setSendingCart(true);
    setCartSent(false); // Reset before sending
    
    console.log('🛒 Sending cart as:', {
      currentUser,
      chatId: chat.id,
      isGuestUser
    });
    
    // Determine user type and guest info
    let userType = 'authenticated';
    let guestInfo = null;
    
    if (isGuestUser && currentUser?.type === 'guest') {
      userType = 'guest';
      
      // Get guest info from localStorage
      const guestSession = localStorage.getItem('guest_session');
      if (guestSession) {
        try {
          guestInfo = JSON.parse(guestSession);
          console.log('🎭 Using guest info:', guestInfo);
        } catch (error) {
          console.error('Error parsing guest session:', error);
        }
      }
    }
    
    console.log(`📤 Calling sendCart as ${userType} user`);
    
    // Call the API - it handles everything
    const result = await TransactionService.sendCart(chat.id, userType, guestInfo);
    
    console.log('✅ Cart sent successfully:', result);

    // ✅ IMMEDIATELY UPDATE LOCAL STATE
    setCartSent(true);
    
    // ✅ FORCE RE-RENDER BY UPDATING CHAT OBJECT
    if (chat && typeof chat === 'object') {
      // Update the chat object to trigger re-render
      chat.status = 'pending_seller_acceptance';
      chat.isCart = false;
    }
    
  } catch (error) {
    console.error('Error sending cart:', error);
    alert('Error: ' + error.message);
    setCartSent(false); // Reset on error
  } finally {
    setSendingCart(false);
  }
};


  
useEffect(() => {
  if (!chat) return;
  
  // ✅ Skip validation if currentUser is not yet determined
  if (!currentUser) {
    console.log('⏳ Waiting for currentUser to be determined...');
    return;
  }
  
  const guestSession = localStorage.getItem('guest_session');
  const parsedGuestSession = guestSession ? JSON.parse(guestSession) : null;
  
  console.log('👤 ChatView user validation:', {
    chatId: chat?.id,
    chatBuyerId: chat?.buyerId,
    chatSellerId: chat?.sellerId,
    currentUserType: currentUser?.type,
    currentUserId: currentUser?.id,
    isGuestOrder: chat?.isGuestOrder
  });
  
  // ✅ FIX: For guest orders, check authorization using isGuestOrder flag
  if (chat?.isGuestOrder === true) {
    console.log('📱 This is a guest order chat');
    
    // Check if authenticated seller (or shared staff acting as seller) viewing guest order
    if (auth.currentUser && !auth.currentUser.isAnonymous && (
      chat.sellerId === auth.currentUser.uid || currentUser?.role === 'seller'
    )) {
      console.log('✅ Authenticated seller viewing guest order - ALLOWED');
      return;
    }
    
    // Check if guest buyer viewing their own order
    if (currentUser?.type === 'guest' || auth.currentUser?.isAnonymous) {
      const guestIdMatches = 
        currentUser.id === chat.buyerId ||
        (parsedGuestSession && parsedGuestSession.guestId === chat.buyerId);
      
      if (guestIdMatches) {
        console.log('✅ Guest buyer viewing their own order - ALLOWED');
        return;
      }
      
      console.error('❌ Guest ID mismatch:', {
        currentUserId: currentUser.id,
        chatBuyerId: chat.buyerId,
        sessionGuestId: parsedGuestSession?.guestId
      });
    }
    
    // Only redirect if we're SURE it's unauthorized
    console.error('❌ Unauthorized access to guest order');
    alert('Access denied: This chat does not belong to you');
    navigate('/messages', { replace: true });
    return;
    
  } else if (!chat?.isGuestOrder) {
    // Regular authenticated user chat
    if (auth.currentUser && !auth.currentUser.isAnonymous) {
      // A real participant's UID is in chat.participants. Shared-shop staff and
      // sub-shop owners are NOT in participants (only the shop's own UID is), but
      // currentUser.role is already resolved above (via isActingAsShop / chat.roles)
      // for exactly those cases, so trust it here too.
      const isParticipant = chat?.participants?.includes(auth.currentUser.uid) || !!currentUser?.role;
      if (!isParticipant) {
        console.error('❌ User is not a participant in this chat');
        alert('Access denied: You are not a participant in this chat');
        navigate('/messages', { replace: true });
      }
    } else {
      // No regular auth user - check if guest trying to access non-guest chat
      if (currentUser?.type === 'guest' || auth.currentUser?.isAnonymous) {
        navigate('/messages', { replace: true });
      } else {
        console.error('❌ No authentication found');
        navigate('/auth', { replace: true });
      }
    }
  }
}, [chat?.id, chat?.buyerId, chat?.isGuestOrder, currentUser?.id, currentUser?.type, currentUser?.role, navigate]);

  // Add this to cleanup on unmount
  useEffect(() => {
    return () => {
      unsubscribeRefs.current.forEach(unsubscribe => unsubscribe());
      unsubscribeRefs.current = [];
      chatIdRef.current = null;
    };
  }, []);
  
  
  
  
  // ✅ CORRECT: Check if message is sent by current user
  const isMessageSentByUser = useCallback((message) => {
    if (!currentUser) return false;
    return message.sender === currentUser.id;
  }, [currentUser]);
  
  // Show location prompt after buyer pays
  useEffect(() => {
    if (chat?.isSeller && transactionDetails?.status === 'paid') {
      const hasAskedForLocation = messages.some(m => m.type === 'buyer_asks_location');
      const hasSharedLocation = messages.some(m => m.type === 'pickup_location_shared');
      
      if (hasAskedForLocation && !hasSharedLocation) {
        setShowLocationPrompt(true);
      }
    }
  }, [chat?.isSeller, transactionDetails, messages]);

  useEffect(() => {
    if (transactionDetails) {
      console.log('Transaction Details in ChatView:', {
        id: transactionDetails.id,
        status: transactionDetails.status,
        unitPrice: transactionDetails.unitPrice,
        requestedQuantity: transactionDetails.requestedQuantity,
        approvedQuantity: transactionDetails.approvedQuantity,
        subtotal: transactionDetails.subtotal,
        total: transactionDetails.total,
        finalTotalPrice: transactionDetails.finalTotalPrice,
        totalPrice: transactionDetails.totalPrice,
        allFields: transactionDetails
      });
    }
  }, [transactionDetails]);

  // Get item availability for seller
  useEffect(() => {
    const fetchAvailability = async () => {
      if (chat?.isSeller && transactionDetails?.itemId && transactionDetails?.sellerId) {
        try {
          const shopRef = doc(db, 'shops', transactionDetails.sellerId);
          const shopSnap = await getDoc(shopRef);
          
          if (shopSnap.exists()) {
            const shopData = shopSnap.data();
            const item = shopData.items?.find(item => item.id === transactionDetails.itemId);
            const available = parseInt(item?.quantity) || 0;
            
            const maxAllowed = Math.min(transactionDetails.requestedQuantity, available);
            setMaxAvailableQuantity(maxAllowed);
          }
        } catch (error) {
          console.error('Error fetching availability:', error);
        }
      }
    };

    fetchAvailability();
  }, [transactionDetails, chat?.isSeller]);

  useEffect(() => {
  // ✅ Wait for currentUser to be determined
  if (!currentUser || !chat) return;
  
  const urlParams = new URLSearchParams(window.location.search);
  const scannedCode = urlParams.get('code');
  const chatIdFromUrl = urlParams.get('chat');
  
  console.log('📱 QR code detection check:', {
    hasCode: !!scannedCode,
    scannedCode,
    chatIdFromUrl,
    currentChatId: chat?.id,
    currentUserRole: currentUser?.role,  // ✅ Log the role
    isSeller: currentUser?.role === 'seller',
    transactionId: chat?.transactionId,
    urlMatches: chat?.id === chatIdFromUrl
  });
  
  // ✅ CRITICAL: Check all conditions including role
  if (scannedCode && 
      chatIdFromUrl && 
      currentUser?.role === 'seller' &&
      chat?.id === chatIdFromUrl) {
    
    console.log('✅ QR code scanned by SELLER, triggering redemption:', {
      code: scannedCode,
      chatId: chatIdFromUrl,
      currentChatId: chat.id,
      transactionId: chat.transactionId,
      userRole: currentUser.role
    });
    
    setRedeemCode(scannedCode);
    setShowRedeemModal(true);
    
    // ✅ Auto-verify after modal opens
    setTimeout(() => {
      console.log('🔐 Auto-verifying code:', scannedCode);
      handleRedeemCode(scannedCode);
    }, 800);  // ✅ Increased timeout to ensure modal is ready
    
    // Clean URL but keep chat parameter
    const newUrl = `${window.location.pathname}?chat=${chatIdFromUrl}`;
    window.history.replaceState({}, '', newUrl);
    
  } else if (scannedCode && chatIdFromUrl) {
    // Debug why it didn't trigger
    console.warn('⚠️ QR code found but not auto-verifying:', {
      hasChat: !!chat,
      hasCurrentUser: !!currentUser,
      chatId: chat?.id,
      currentUserRole: currentUser?.role,
      isSeller: currentUser?.role === 'seller',
      urlChatId: chatIdFromUrl,
      chatIdMatches: chat?.id === chatIdFromUrl,
      allConditionsMet: !!(scannedCode && chatIdFromUrl && currentUser?.role === 'seller' && chat?.id === chatIdFromUrl),
      failureReason: !chat ? 'No chat loaded' :
                     !currentUser ? 'No currentUser' :
                     currentUser?.role !== 'seller' ? `User is ${currentUser?.role}, not seller` :
                     chat.id !== chatIdFromUrl ? `Chat ID mismatch: ${chat.id} !== ${chatIdFromUrl}` :
                     'Unknown'
    });
    
    // ✅ If buyer scanned their own QR, just show a message
    if (currentUser?.role === 'buyer') {
      console.log('ℹ️ Buyer scanned their own QR code - this is normal, no action needed');
    }
  }
}, [chat?.id, currentUser?.role, currentUser?.id]);

  // Seller respond
  const handleSellerResponse = async (decision, adjustedQuantity = null) => {
  // ⚠️ Stripe verification disabled — all orders use the mock payment flow for now.
  try {
    setLoading(true);
    console.log('🎯 Seller response:', { decision });

    await TransactionService.respondToQuantityRequest(
      chat.transactionId,
      decision,
      decision === 'accept' ? adjustedQuantity : null
    );

    setShowSellerPrompt(false);
  } catch (error) {
    console.error('❌ Error in seller response:', error);
    alert('Error: ' + error.message);
  } finally {
    setLoading(false);
  }
};

  // Handle send message
  const handleSendMessage = async () => {
  if (!inputMessage.trim() || !chat || !currentUser || chat.__pendingCreation) return;
  
  try {
    const otherPartyId = chat.isBuyer ? chat.sellerId : chat.buyerId;
    
    // Add message
    // sender uses the shop's ID when acting as a shared shop seller,
    // senderUid tracks the real user for audit purposes
    await addDoc(collection(db, 'chats', chat.id, 'messages'), {
      text: inputMessage.trim(),
      sender: currentUser.id,         // shop ID when acting as shop, own UID otherwise
      senderUid: currentUser.senderUid || currentUser.id,  // always real UID
      senderName: currentUser.name,
      timestamp: serverTimestamp(),
      type: 'text'
    });
    
    // Update chat metadata
    await updateDoc(doc(db, 'chats', chat.id), {
      lastMessage: inputMessage.trim(),
      lastMessageTime: serverTimestamp(),
      [`unreadCount.${otherPartyId}`]: increment(1),
      updatedAt: serverTimestamp()
    });
    
    setInputMessage('');
    
  } catch (error) {
    console.error('❌ Error sending message:', error);
    alert('Failed to send message: ' + error.message);
  }
};

  // Payment
  const handlePayment = async (transactionCode, qrCodeUrl) => {
  try {
    setLoading(true);
    
    console.log('💳 Payment completed in ChatView:', {
      transactionCode,
      qrCodeUrl,
      transactionId: chat.transactionId
    });
    
    // ✅ FIX: Don't add QR code here - it's already added by the API or TransactionService!
    // await addQRCodeToChat(transactionCode, qrCodeUrl); // ❌ REMOVE THIS LINE
    
    console.log('✅ QR code already added by payment service, skipping duplicate');
    
    setShowPaymentPrompt(false);
    
    // Refresh transaction data to get latest status
    const transactionDoc = await getDoc(doc(db, 'transactions', chat.transactionId));
    if (transactionDoc.exists()) {
      setTransactionDetails(transactionDoc.data());
    }
    
  } catch (error) {
    console.error('Error handling payment completion:', error);
    alert('Error: ' + error.message);
  } finally {
    setLoading(false);
  }
};

  // Service progress handler — called by UnifiedCartWindow service action buttons
  const handleServiceProgress = async (transactionId, progress) => {
    try {
      setLoading(true);
      if (progress === 'complete') {
        await TransactionService.completeServiceTransaction(transactionId);
      } else {
        await TransactionService.updateServiceProgress(transactionId, progress);
      }
    } catch (err) {
      console.error('Service progress error:', err);
      alert('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Add QR code to chat
  const addQRCodeToChat = async (transactionCode, qrCodeUrl) => {
    try {
      if (!currentUser) {
        throw new Error('No user session found');
      }
      
      // Determine visibility - only buyer should see QR code
      const visibleTo = [currentUser.id];
      
      // Add QR code message to chat
      await addDoc(collection(db, 'chats', chat.id, 'messages'), {
        text: `Payment successful! Your Pickup Kode:`,
        sender: currentUser.id,
        senderName: currentUser.name,
        timestamp: serverTimestamp(),
        type: 'payment_qr_code',
        visibleTo: visibleTo,
        purchaseData: {
          transactionCode: transactionCode,
          qrCodeUrl: qrCodeUrl,
          approvedQuantity: transactionDetails?.approvedQuantity || transactionDetails?.requestedQuantity,
          total: transactionDetails?.total
        }
      });

      console.log('QR code added to chat:', transactionCode);
      
    } catch (error) {
      console.error('Error adding QR code to chat:', error);
      throw error;
    }
  };

  // Withdraw
  const handleWithdraw = async () => {
    if (!window.confirm('Cancel this order and withdraw your payment?')) return;

    try {
      setLoading(true);
      await TransactionService.withdrawPayment(chat.transactionId);
    } catch (error) {
      console.error('Error withdrawing:', error);
      alert('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Location sharing
  const handleLocationSelect = async (locationAddress) => {
    try {
      setLoading(true);
      await TransactionService.sharePickupLocation(chat.transactionId, locationAddress);
      setShowLocationPrompt(false);
    } catch (error) {
      console.error('Error sharing location:', error);
      alert('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Redeem code
  const handleRedeemCode = async (codeOverride = null) => {
  const cleanCode = (codeOverride || redeemCode).trim();
  
  console.log('🔐 Redeem code called:', {
    codeOverride,
    redeemCode,
    cleanCode,
    transactionId: chat?.transactionId
  });
  
  if (cleanCode === 'KODE-' || cleanCode.length <= 5) {
    setRedeemError('Please enter the complete code');
    return;
  }
  
  // ✅ Validate code format
  if (cleanCode.length !== 11 || !cleanCode.startsWith('KODE-')) {
    setRedeemError('Invalid code format. Should be KODE-XXXXXX');
    return;
  }

  try {
    setLoading(true);
    setRedeemError(null);
    
    console.log('🔐 Verifying code:', cleanCode, 'for transaction:', chat?.transactionId);
    
    await TransactionService.completeTransaction(chat.transactionId, cleanCode);
    
    setRedeemSuccess(`✅ Transaction completed! Funds released.`);
    
    setTimeout(() => {
      setShowRedeemModal(false);
      setRedeemCode('KODE-');
      setRedeemSuccess(null);
    }, 2000);
    
  } catch (error) {
    console.error('Redeem error:', error);
    setRedeemError(error.message || 'Invalid code. Please try again.');
  } finally {
    setLoading(false);
  }
};

  // Camera functions
  const startCamera = async () => {
    try {
      setShowCamera(true);
      setCameraError(null);
      
      await new Promise(resolve => setTimeout(resolve, 400));
      
      if (!videoRef.current) {
        throw new Error('Video element not found');
      }

      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920, min: 640 },
          height: { ideal: 1080, min: 480 }
        } 
      });
      
      videoRef.current.srcObject = stream;
      
      await new Promise((resolve, reject) => {
        videoRef.current.onloadedmetadata = resolve;
        videoRef.current.onerror = reject;
        setTimeout(reject, 5000);
      });
      
      await videoRef.current.play();

      qrScannerRef.current = new QrScanner(
  videoRef.current,
  (result) => {
    try {
      const scannedData = result.data;
      let extractedCode = null;
      let extractedChatId = null;
      
      console.log('📷 QR code scanned:', scannedData);
      
      // Try to parse as URL
      if (scannedData.includes('http://') || scannedData.includes('https://')) {
        try {
          const urlObj = new URL(scannedData);
          extractedCode = urlObj.searchParams.get('code');
          extractedChatId = urlObj.searchParams.get('chat');
          
          console.log('✅ Parsed URL:', {
            code: extractedCode,
            chatId: extractedChatId,
            fullURL: scannedData
          });
        } catch (e) {
          console.warn('⚠️ Failed to parse as URL:', e);
        }
      }
      
      // Fallback: Try to extract code directly
      if (!extractedCode && scannedData.startsWith('KODE-')) {
        extractedCode = scannedData;
      }
      
      // Fallback: Search for KODE- pattern
      if (!extractedCode && scannedData.includes('KODE-')) {
        const match = scannedData.match(/KODE-[A-Z0-9]{6}/);
        if (match) {
          extractedCode = match[0];
        }
      }
      
      console.log('🔍 Extraction results:', {
        extractedCode,
        extractedChatId,
        currentChatId: chat?.id
      });
      
      // Validate code format
      if (extractedCode && extractedCode.length === 11) {
        // Stop scanner
        if (qrScannerRef.current) {
          qrScannerRef.current.stop();
        }
        
        // ✅ CRITICAL: Verify this is the correct chat
        // Compare extractedChatId with chat.id (NOT chat.transactionId)
        if (extractedChatId && extractedChatId !== chat?.id) {
          console.warn('⚠️ Chat ID mismatch:', {
            scannedChatId: extractedChatId,
            currentChatId: chat?.id,
            transactionId: chat?.transactionId
          });
          setCameraError(`This QR code is for a different order. Please open that order first.`);
          
          setTimeout(() => {
            setShowCamera(false);
            cleanupCamera();
            
            // ✅ Navigate to the correct chat
            if (extractedChatId) {
              window.location.href = `/messages?chat=${extractedChatId}&code=${extractedCode}`;
            }
          }, 2000);
          return;
        }
        
        setRedeemCode(extractedCode);
        setCameraError(`✅ Code detected: ${extractedCode}`);
        
        setTimeout(() => {
          setShowCamera(false);
          cleanupCamera();
          setCameraError(null);
          
          // Open redeem modal
          setShowRedeemModal(true);
          
          // ✅ Auto-verify
          setTimeout(() => {
            handleRedeemCode(extractedCode);
          }, 500);
        }, 1000);
        
      } else {
        console.error('❌ Invalid code format:', {
          extractedCode,
          length: extractedCode?.length,
          expected: 11
        });
        setCameraError('Invalid QR code format. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error parsing QR code:', error);
      setCameraError('Error reading QR code. Please try again.');
    }
  },
  {
    returnDetailedScanResult: true,
    highlightScanRegion: true,
    highlightCodeOutline: true,
    maxScansPerSecond: 10,
    preferredCamera: 'environment'
  }
);

      await qrScannerRef.current.start();
      setCameraError('📸 Point camera at QR code...');
      
    } catch (error) {
      console.error('Camera error:', error);
      
      let errorMessage = 'Unable to start camera. ';
      
      if (error.name === 'NotAllowedError') {
        errorMessage += 'Please allow camera access in your browser settings.';
      } else if (error.name === 'NotFoundError') {
        errorMessage += 'No camera found on this device.';
      } else if (error.name === 'NotReadableError') {
        errorMessage += 'Camera is already in use by another app.';
      } else {
        errorMessage += error.message;
      }
      
      setCameraError(errorMessage);
      
      setTimeout(() => {
        setShowCamera(false);
        cleanupCamera();
      }, 3000);
    }
  };

  const stopCamera = () => {
    cleanupCamera();
    setShowCamera(false);
    setCameraError(null);
  };

  const cleanupCamera = () => {
    if (qrScannerRef.current) {
      qrScannerRef.current.destroy();
      qrScannerRef.current = null;
    }
    
    if (videoRef.current?.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
  };

  useEffect(() => {
    return cleanupCamera;
  }, []);

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      date = new Date(timestamp);
    }
    
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // In ChatView.js - Update the renderMessage function:

// In src/pages/shop/components/ChatView.js

const renderMessage = useCallback((message) => {
  if (!currentUser) return null;
  
  const isSystemMessage = message.sender === 'system';
  const isSentByUser = message.sender === currentUser.id;
  
  // ✅ FIX: Simple visibility check
  if (message.visibleTo && message.visibleTo.length > 0) {
    // Just check if current user's ID is in the array
    if (!message.visibleTo.includes(currentUser.id)) {
      console.log('⏭️ Message not visible to user:', message.id);
      return null;
    }
  }
  
  // Hide old message types
  if ([
    'buyer_inquiry',
    'order_accepted',
    'order_rejected',
    'buyer_asks_location',
    'transaction_completed'
  ].includes(message.type)) {
    return null;
  }

  // Order details (item/qty/customization) and cart-item-added notices are
  // shown in the order card (UnifiedCartWindow) instead — keep the chat
  // thread to messages actually exchanged between buyer and seller.
  if (['order_summary', 'item_added'].includes(message.type)) {
    return null;
  }
  
  // QR code is shown in the cart window — suppress chat bubble
  if (message.type === 'payment_qr_code') {
    return null;
  }
  
  // Pickup location shared (keep this)
  if (message.type === 'pickup_location_shared') {
    return (
      <Message 
        key={message.id}
        sent={isSentByUser}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
          <MapPin size={16} style={{ marginTop: '0.2rem', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            {message.text}
          </div>
        </div>
        {message.timestamp && (
          <div className="message-time">
            {formatTimestamp(message.timestamp)}
          </div>
        )}
      </Message>
    );
  }
  
  // Order cancelled message
  if (message.type === 'order_cancelled') {
    return (
      <Message 
        key={message.id}
        className="system-message"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <AlertCircle size={16} />
          <span>{message.text}</span>
        </div>
        {message.timestamp && (
          <div className="message-time">
            {formatTimestamp(message.timestamp)}
          </div>
        )}
      </Message>
    );
  }
  
  // Escrow confirmed message
  if (message.type === 'escrow_confirmed') {
    return (
      <Message 
        key={message.id}
        className="system-message"
      >
        <div style={{ whiteSpace: 'pre-wrap' }}>
          {message.text}
        </div>
        {message.timestamp && (
          <div className="message-time">
            {formatTimestamp(message.timestamp)}
          </div>
        )}
      </Message>
    );
  }
  
  // Regular text message
  return (
    <Message 
      key={message.id} 
      sent={isSentByUser}
      className={isSystemMessage ? 'system-message' : ''}
    >
      {message.text}
      {message.timestamp && (
        <div className="message-time">
          {formatTimestamp(message.timestamp)}
        </div>
      )}
    </Message>
  );
}, [currentUser, transactionDetails]);

  // Computed once so both the rendered list and the "nothing here yet" empty
  // state agree on what's actually visible — several message types (order
  // summary, item-added, etc.) exist in `messages` but render as null.
  const renderedMessages = useMemo(
    () => messages.map(renderMessage).filter(Boolean),
    [messages, renderMessage]
  );

  if (!chat || !currentUser) {
    console.log('❌ No chat or current user, returning null');
    return null;
  }

  const isMock = transactionDetails?.useMockStripe;

  return (
  <ChatContainer>
    <ChatHeader>
      <div className="left-section">
        <div className="avatar">
  {chat.isGuestOrder && chat.role === 'seller' ? (
    <User size={20} />
  ) : otherPartyProfile ? (
    <>
      <img 
        src={otherPartyProfile} 
        alt={chat.otherPartyName || 'Profile'}
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'flex';
        }}
      />
      <User size={20} style={{ display: 'none' }} />  
    </>
  ) : (
    <User size={20} />
  )}
</div>
        
        <div className="chat-details">
          <div className="seller-info">
            {currentUser?.role === 'seller'
              ? (chat.buyerName || chat.otherPartyName || 'Unknown Buyer')
              : (chat.sellerName || chat.otherPartyName || 'Unknown Store')}
            {isGuestUser && (
              <span style={{
                marginLeft: '0.5rem',
                padding: '0.1rem 0.4rem',
                background: 'rgba(255, 193, 7, 0.2)',
                color: '#FFC107',
                borderRadius: '4px',
                fontSize: '1rem',
                fontWeight: '600',
                textTransform: 'uppercase'
              }}>
                Guest
              </span>
            )}
          </div>
          <div className="item-name">
            {currentUser?.role === 'seller' && chat.sellerName
              ? `${chat.sellerName} · ${getItemCount(chat)} ${getItemCount(chat) === 1 ? 'item' : 'items'}`
              : (() => {
                  const itemCount = getItemCount(chat);
                  return `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`;
                })()}
          </div>
        </div>
      </div>

      <div className="header-actions">
        {shouldShowContent('withdraw_button', chat, currentUser) && (
          <HeaderButton onClick={handleWithdraw} disabled={loading}>
            <span className="button-text">Withdraw</span>
          </HeaderButton>
        )}

        {currentUser?.role === 'seller' && chat?.sellerId && (
          <PaymentHandlesWrap>
            <HeaderButton onClick={() => setShowPaymentHandles(o => !o)}>
              <CreditCard size={16} />
              <span className="button-text">Payment Info</span>
            </HeaderButton>
            {showPaymentHandles && (
              <PaymentHandlesDropdown>
                <div className="title">Pickup Payment Info</div>
                <div className="hint">Shown to buyers as options when paying you at pickup.</div>

                <label>Zelle ID</label>
                <input
                  type="text"
                  value={paymentHandlesForm.zelleId}
                  onChange={e => { setPaymentHandlesForm(f => ({ ...f, zelleId: e.target.value })); setHandlesSaved(false); }}
                  placeholder="e.g. 555-123-4567"
                />

                <label>Venmo ID</label>
                <input
                  type="text"
                  value={paymentHandlesForm.venmoId}
                  onChange={e => { setPaymentHandlesForm(f => ({ ...f, venmoId: e.target.value })); setHandlesSaved(false); }}
                  placeholder="e.g. @your-venmo"
                />

                <label>Other</label>
                <input
                  type="text"
                  value={paymentHandlesForm.other}
                  onChange={e => { setPaymentHandlesForm(f => ({ ...f, other: e.target.value })); setHandlesSaved(false); }}
                  placeholder="e.g. CashApp $yourtag"
                />

                <button className="save-btn" onClick={handleSavePaymentHandles} disabled={savingHandles}>
                  {savingHandles ? 'Saving…' : 'Save'}
                </button>
                {handlesSaved && <div className="saved-msg">✅ Saved — buyers will see this now.</div>}
              </PaymentHandlesDropdown>
            )}
          </PaymentHandlesWrap>
        )}

        {shouldShowContent('redeem_button', chat, currentUser) && (
          <HeaderButton primary redeem onClick={() => setShowRedeemModal(true)}>
            <QrCode size={16} />
            <span className="button-text">Redeem</span>
          </HeaderButton>
        )}
      </div>
    </ChatHeader>

    {/* No-login guest order — there's no live buyer on the other end of this
        thread (buyerId is null), so show their contact info directly instead
        of a message composer that implies they'll read a reply in-app. */}
    {currentUser?.role === 'seller' && chat?.isGuestOrder && !chat?.buyerId && chat?.guestContact && (
      <div style={{
        margin: '0.75rem 1rem 0', padding: '0.75rem 0.9rem',
        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 12, display: 'flex', flexDirection: 'column', gap: '0.4rem',
      }}>
        <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.6 }}>
          Guest order — no account, reach out directly
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.82rem', fontWeight: 600 }}>
            <User size={14} /> {chat.guestContact.name}
          </span>
          {chat.guestContact.phone && (
            <a href={`tel:${chat.guestContact.phone}`} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.82rem', color: 'inherit', textDecoration: 'none', opacity: 0.85 }}>
              <Phone size={14} /> {chat.guestContact.phone}
            </a>
          )}
          {chat.guestContact.email && (
            <a href={`mailto:${chat.guestContact.email}`} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.82rem', color: 'inherit', textDecoration: 'none', opacity: 0.85 }}>
              <Mail size={14} /> {chat.guestContact.email}
            </a>
          )}
        </div>
        {(chat.guestContact.address || chat.guestContact.notes) && (
          <div style={{ fontSize: '0.78rem', opacity: 0.75, lineHeight: 1.5 }}>
            {chat.guestContact.address && <div>📍 {chat.guestContact.address}</div>}
            {chat.guestContact.notes && <div>📝 {chat.guestContact.notes}</div>}
          </div>
        )}
      </div>
    )}

    <ChatBody
  ref={chatBodyRef}
  onScroll={() => {
    const element = chatBodyRef.current;
    if (element) {
      const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
      isScrolledUpRef.current = !isAtBottom;
    }
  }}
>
  {/* ✅ Unified Cart Window - shows throughout entire transaction.
      Gated on the latched `hasOrderWindow` flag (not the raw fields directly)
      so transient listener races can't unmount/remount it — see effect above. */}
  {hasOrderWindow && (
    <Message className="system-action-window">
      <UnifiedCartWindow
        key={chat?.sellerId && chat?.buyerId ? `${chat.sellerId}_${chat.buyerId}` : chat?.id}
        transaction={transactionDetails}
        chat={chat}
        currentUser={currentUser}
        onSendCart={handleSendCart}
        onRespond={handleSellerResponse}
        onPayment={handlePayment}
        onServiceProgress={handleServiceProgress}
        onLocationSelect={handleLocationSelect}
        loading={loading || !!chat?.__pendingCreation}
        isMock={isMock}
      />
    </Message>
  )}

  {chat?.__pendingCreation && messages.length === 0 && (
    <PendingCreationRow>
      <div className="spinner" />
      Placing your order…
    </PendingCreationRow>
  )}

  {/* Regular messages */}
  {renderedMessages}

  {renderedMessages.length === 0 && !chat?.__pendingCreation && (
    <EmptyChatRoom onClick={() => chatInputRef.current?.focus()}>
      <ChevronDown size={18} />
      <span>
        {(chat?.isGuestOrder && !chat?.buyerId)
          ? 'No live buyer here — use the contact info above. Notes below are private.'
          : `Chat with ${currentUser?.role === 'seller' ? 'buyer' : 'seller'}`}
      </span>
    </EmptyChatRoom>
  )}

  {/* ❌ REMOVE these old sections - now in UnifiedCartWindow:
  - Cart summary
  - Send cart button
  - Seller response window
  - Payment window
  - Location window
  */}

  <div ref={messagesEndRef} />
</ChatBody>

    {shouldShowContent('chat_input', chat, currentUser) && (
      <ChatInput>
        <input
          ref={chatInputRef}
          type="text"
          placeholder={(chat?.isGuestOrder && !chat?.buyerId) ? "Add a private note (the guest won't see this)..." : "Type a message..."}
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
        />
        
        <button
          className="send-button"
          onClick={handleSendMessage}
          disabled={!inputMessage.trim() || !!chat?.__pendingCreation}
        >
          <Send size={16} />
        </button>
      </ChatInput>
    )}

      {/* Redeem Modal */}
      {showRedeemModal && (
        <RedeemModal onClick={(e) => e.target === e.currentTarget && setShowRedeemModal(false)}>
          <div className="modal-content">
            <h3>
              <QrCode size={20} />
              Redeem Pickup Code
            </h3>

            <div className="code-input-section">
              <label>Enter buyer's pickup code:</label>
              <input
                type="text"
                value={redeemCode}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  if (value.startsWith('KODE-') || value === '') {
                    setRedeemCode(value === '' ? 'KODE-' : value);
                  }
                }}
                onFocus={(e) => {
                  if (e.target.value === 'KODE-') {
                    setTimeout(() => e.target.setSelectionRange(5, 5), 0);
                  }
                }}
                maxLength={11}
                placeholder="KODE-XXXXXX"
              />
            </div>

            {redeemError && (
              <div className="error-message">{redeemError}</div>
            )}

            {redeemSuccess && (
              <div className="success-message">{redeemSuccess}</div>
            )}

            <div className="modal-buttons">
              <button 
                className="cancel"
                onClick={() => {
                  setShowRedeemModal(false);
                  setRedeemCode('KODE-');
                  setRedeemError(null);
                }}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                className="scan"
                onClick={startCamera}
                disabled={loading}
              >
                <Camera size={16} />
                Scan QR
              </button>
              <button 
                className="verify"
                onClick={() => handleRedeemCode()}
                disabled={loading || redeemCode.length <= 5}
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </div>
        </RedeemModal>
      )}

      {/* Camera Modal */}
      {showCamera && (
        <CameraModal>
          <div className="camera-header">
            <h3>Scan Buyer's QR Code</h3>
            <p>Point camera at the QR code and hold steady</p>
            {cameraError && (
              <div style={{
                color: cameraError.includes('✅') ? '#4CAF50' : '#F44336',
                background: cameraError.includes('✅') ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                padding: '0.75rem',
                borderRadius: '8px',
                marginTop: '1rem',
                fontSize: '0.9rem',
                border: `1px solid ${cameraError.includes('✅') ? 'rgba(76, 175, 80, 0.4)' : 'rgba(244, 67, 54, 0.4)'}`
              }}>
                {cameraError}
              </div>
            )}
          </div>

          <div className="camera-view">
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline 
              muted
              style={{ transform: 'scaleX(1)' }}
            />
            <div className="scan-overlay" />
          </div>

          <div className="camera-controls">
            <button onClick={stopCamera}>
              <X size={16} />
              Stop Camera
            </button>
          </div>
        </CameraModal>
      )}
    </ChatContainer>
  );
};

export default ChatView;